import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatDateShort } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';
import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';

import BooleanChip from './BooleanChip';
import SurchargeRoomsCell from './SurchargeRoomsCell';
import { getApplicableNightCount } from './surchargeUtils';
import type { SurchargesTabProps } from './types';

export default function SurchargesTab({
  appliedRoomsBySurchargeId,
  canAddSurcharges,
  error,
  isLoading,
  isLoadingRooms,
  onAddSurcharges,
  surcharges,
}: SurchargesTabProps) {
  useDealInformationTabCount('surcharges', isLoading || error ? undefined : surcharges.length);

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading surcharges.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load Salesforce surcharges.</Alert>;
  }

  return (
    <TabPanelShell
      title="Surcharges"
      subtitle="Salesforce surcharge periods linked to this offer."
      actions={
        <Button
          disabled={!canAddSurcharges}
          onClick={onAddSurcharges}
          startIcon={<AddIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          variant="contained"
        >
          Add surcharges to room rates
        </Button>
      }
      contentSx={{ p: 0 }}
    >
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 1500 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50',
                  color: 'text.secondary',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Days of week</TableCell>
              <TableCell align="center">Nights</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Converted Cost</TableCell>
              <TableCell align="right">Sell Price</TableCell>
              <TableCell align="right">Margin</TableCell>
              <TableCell>Collection</TableCell>
              <TableCell align="center">Accumulate</TableCell>
              <TableCell align="center">Overlapping</TableCell>
              <TableCell>Rooms</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {surcharges.length === 0 && (
              <TableRow>
                <TableCell colSpan={14}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce surcharges found for this offer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {surcharges.map((surcharge, index) => {
              const margin = surcharge.margin;
              const hasMargin = typeof margin === 'number';

              return (
                <TableRow key={surcharge.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{surcharge.name}</TableCell>
                  <TableCell>{formatDateShort(surcharge.start_date)}</TableCell>
                  <TableCell>{formatDateShort(surcharge.end_date)}</TableCell>
                  <TableCell>{daysOfWeekMessage(surcharge)}</TableCell>
                  <TableCell align="center">{getApplicableNightCount(surcharge)}</TableCell>
                  <TableCell align="right">
                    {currencyFormatter(surcharge.currency_code, surcharge.cost_price)}
                  </TableCell>
                  <TableCell align="right">{currencyFormatter('AUD', surcharge.converted_cost)}</TableCell>
                  <TableCell align="right">
                    {currencyFormatter(surcharge.currency_code, surcharge.sell_price)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      color={hasMargin ? (margin >= 20 ? 'success.main' : 'warning.main') : 'error.main'}
                      fontWeight={700}
                      variant="body2"
                    >
                      {hasMargin ? `${margin.toFixed(1)}%` : 'Missing'}
                    </Typography>
                  </TableCell>
                  <TableCell>{surcharge.collected_by}</TableCell>
                  <TableCell align="center">
                    <BooleanChip value={surcharge.accumulate} />
                  </TableCell>
                  <TableCell align="center">
                    <BooleanChip value={surcharge.overlapping} />
                  </TableCell>
                  <TableCell>
                    <SurchargeRoomsCell
                      isLoading={isLoadingRooms}
                      rooms={appliedRoomsBySurchargeId[surcharge.id] ?? []}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
